'use client';

import { useState } from 'react';

interface WeddingState {
  guests: number;
  venuePerPerson: number;
  catering: number;
  photography: number;
  flowers: number;
  entertainment: number;
}

const INITIAL_STATE: WeddingState = {
  guests: 150,
  venuePerPerson: 100,
  catering: 75,
  photography: 3000,
  flowers: 2000,
  entertainment: 2500,
};

const PRESETS: { [key: string]: WeddingState } = {
  conservative: {
    guests: 75,
    venuePerPerson: 75,
    catering: 50,
    photography: 1500,
    flowers: 1000,
    entertainment: 1500,
  },
  moderate: {
    guests: 150,
    venuePerPerson: 100,
    catering: 75,
    photography: 3000,
    flowers: 2000,
    entertainment: 2500,
  },
  aggressive: {
    guests: 250,
    venuePerPerson: 150,
    catering: 125,
    photography: 5000,
    flowers: 4000,
    entertainment: 5000,
  },
};

export default function CalculatorWedding() {
  const [inputs, setInputs] = useState<WeddingState>(INITIAL_STATE);

  const calculate = () => {
    const venueTotal = inputs.guests * inputs.venuePerPerson;
    const cateringTotal = inputs.guests * inputs.catering;
    const totalCost = venueTotal + cateringTotal + inputs.photography + inputs.flowers + inputs.entertainment;
    const perGuest = totalCost / inputs.guests;

    return {
      totalCost,
      venueTotal,
      cateringTotal,
      perGuest,
    };
  };

  const results = calculate();

  const handleReset = () => {
    setInputs(INITIAL_STATE);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    setInputs(PRESETS[preset]);
  };

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-2">
        <div className="bg-white rounded-3xl p-8 shadow-modern-xl border border-slate-200 sticky top-24">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-2 h-8 bg-gradient-to-b from-pink-500 to-rose-600 rounded-full"></div>
            <h2 className="text-2xl font-bold text-slate-900">Wedding Details</h2>
          </div>

          {/* Preset and Reset Buttons */}
          <div className="mb-6 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handlePreset('conservative')}
                className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
              >
                Conservative
              </button>
              <button
                onClick={() => handlePreset('moderate')}
                className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
              >
                Moderate
              </button>
              <button
                onClick={() => handlePreset('aggressive')}
                className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
              >
                Aggressive
              </button>
            </div>
            <button
              onClick={handleReset}
              className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
            >
              Reset to Default
            </button>
          </div>

          <div className="space-y-6">
            <InputField
              label="Number of Guests"
              value={inputs.guests}
              onChange={(v) => setInputs({ ...inputs, guests: v })}
              min={1}
              unit="guests"
            />
            <InputField
              label="Venue Cost Per Person"
              value={inputs.venuePerPerson}
              onChange={(v) => setInputs({ ...inputs, venuePerPerson: v })}
              min={0}
              prefix="$"
            />
            <InputField
              label="Catering Per Person"
              value={inputs.catering}
              onChange={(v) => setInputs({ ...inputs, catering: v })}
              min={0}
              prefix="$"
            />
            <InputField
              label="Photography Budget"
              value={inputs.photography}
              onChange={(v) => setInputs({ ...inputs, photography: v })}
              min={0}
              prefix="$"
            />
            <InputField
              label="Flowers & Decorations"
              value={inputs.flowers}
              onChange={(v) => setInputs({ ...inputs, flowers: v })}
              min={0}
              prefix="$"
            />
            <InputField
              label="Entertainment Budget"
              value={inputs.entertainment}
              onChange={(v) => setInputs({ ...inputs, entertainment: v })}
              min={0}
              prefix="$"
            />
          </div>
        </div>
      </div>

      <div className="lg:col-span-3 space-y-6">
        <div className="bg-gradient-to-br from-pink-500 via-rose-500 to-red-600 rounded-3xl p-10 shadow-modern-xl text-white">
          <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-3">
            Total Wedding Cost
          </div>
          <div className="text-6xl font-bold mb-2">
            ${results.totalCost.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </div>
          <div className="text-lg opacity-90">For {inputs.guests} guests</div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <ResultCard
            label="Cost Per Guest"
            value={`$${results.perGuest.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            description="Average per person"
            gradient="from-purple-500 to-pink-600"
          />
          <ResultCard
            label="Venue & Catering"
            value={`$${(results.venueTotal + results.cateringTotal).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            description="Combined guest costs"
            gradient="from-blue-500 to-indigo-600"
          />
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-modern-xl border border-slate-200">
          <h3 className="text-xl font-bold text-slate-900 mb-6">Cost Breakdown</h3>
          <div className="space-y-4">
            <InsightRow
              label="Venue"
              value={`$${results.venueTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
              percentage={((results.venueTotal / results.totalCost) * 100).toFixed(0)}
            />
            <InsightRow
              label="Catering"
              value={`$${results.cateringTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
              percentage={((results.cateringTotal / results.totalCost) * 100).toFixed(0)}
            />
            <InsightRow
              label="Photography"
              value={`$${inputs.photography.toLocaleString('en-US')}`}
              percentage={((inputs.photography / results.totalCost) * 100).toFixed(0)}
            />
            <InsightRow
              label="Flowers & Decorations"
              value={`$${inputs.flowers.toLocaleString('en-US')}`}
              percentage={((inputs.flowers / results.totalCost) * 100).toFixed(0)}
            />
            <InsightRow
              label="Entertainment"
              value={`$${inputs.entertainment.toLocaleString('en-US')}`}
              percentage={((inputs.entertainment / results.totalCost) * 100).toFixed(0)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function InputField({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  prefix,
  unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  unit?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-bold text-slate-700 mb-2">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
            {prefix}
          </span>
        )}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={min}
          max={max}
          step={step}
          className={`w-full ${prefix ? 'pl-8' : 'pl-4'} ${unit ? 'pr-20' : 'pr-4'} py-3.5 border-2 border-slate-200 rounded-xl focus:border-pink-500 focus:ring-4 focus:ring-pink-500/20 outline-none transition-all font-semibold text-slate-900 text-lg`}
        />
        {unit && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

function ResultCard({
  label,
  value,
  description,
  gradient,
}: {
  label: string;
  value: string;
  description: string;
  gradient: string;
}) {
  return (
    <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-6 shadow-modern-lg text-white`}>
      <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">{label}</div>
      <div className="text-4xl font-bold mb-1">{value}</div>
      <div className="text-sm opacity-90">{description}</div>
    </div>
  );
}

function InsightRow({ label, value, percentage }: { label: string; value: string; percentage: string }) {
  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="flex justify-between items-center mb-2">
        <span className="text-slate-600 font-medium">{label}</span>
        <span className="text-slate-900 font-bold text-lg">{value}</span>
      </div>
      <div className="w-full bg-slate-100 rounded-full h-2">
        <div
          className="bg-gradient-to-r from-pink-500 to-rose-600 h-2 rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
      <div className="text-xs text-slate-500 mt-1">{percentage}% of total</div>
    </div>
  );
}